import React from "react";
import { ArrowUpRight, ShieldCheck } from "lucide-react";

import { getSession, login, logout } from "../../api/client";
import styles from "./authBoundary.module.css";
import { SubscriptionGate } from "../subscription/SubscriptionGate";
import { LucimixEntry } from "./LucimixEntry";
import { isEmbedded } from "./lucimixHandshake";

// 登录成功后这么短时间内又被判未登录，说明会话根本没存住（典型：iframe 跨站，
// 浏览器拒收 SameSite=Lax 的 Cookie）。此时再自动重新握手只会一圈圈消耗 janus 票据。
const SESSION_NOT_KEPT_WINDOW_MS = 15_000;


export function AuthBoundary({ children }) {
  // 嵌在 Lucimix iframe 里：不显示登录页，也不复用已有 Cookie 会话——同一个浏览器里
  // 先后切换 Lucimix 账号时，后一个人会看到前一个人的数据，所以每次都用新票据重新验证。
  const [embedded] = React.useState(isEmbedded);
  const [status, setStatus] = React.useState(embedded ? "anonymous" : "checking");
  const [session, setSession] = React.useState(null);
  const [sessionNotKept, setSessionNotKept] = React.useState(false);
  // 订阅已到期/已取消：后端对业务接口统一 403，client.js 把它变成事件，整页换成停用页。
  const [subscriptionBlock, setSubscriptionBlock] = React.useState(null);
  // 订阅恢复后让应用整个重新挂载，各页面重新取数（停用期间它们的请求全是 403）。
  const [remountKey, setRemountKey] = React.useState(0);
  const signedInAt = React.useRef(0);

  const clearAuthentication = React.useCallback(() => {
    if (embedded && Date.now() - signedInAt.current < SESSION_NOT_KEPT_WINDOW_MS) {
      setSessionNotKept(true);
    }
    setSession(null);
    setSubscriptionBlock(null);
    setStatus("anonymous");
  }, [embedded]);

  const handleLucimixSignedIn = React.useCallback((payload) => {
    signedInAt.current = Date.now();
    setSession(payload);
    setStatus("authenticated");
  }, []);

  React.useEffect(() => {
    if (embedded) return undefined;
    let active = true;
    void getSession()
      .then((payload) => {
        if (!active) return;
        setSession(payload);
        setStatus("authenticated");
      })
      .catch(() => {
        if (active) clearAuthentication();
      });
    return () => {
      active = false;
    };
  }, [clearAuthentication, embedded]);

  React.useEffect(() => {
    window.addEventListener("astock:authentication-required", clearAuthentication);
    return () => {
      window.removeEventListener("astock:authentication-required", clearAuthentication);
    };
  }, [clearAuthentication]);

  React.useEffect(() => {
    const onBlocked = (event) => setSubscriptionBlock(event.detail);
    window.addEventListener("astock:subscription-blocked", onBlocked);
    return () => window.removeEventListener("astock:subscription-blocked", onBlocked);
  }, []);

  async function handleLogin(username, password) {
    const payload = await login(username, password);
    setSession(payload);
    setStatus("authenticated");
  }

  async function handleLogout() {
    try {
      await logout();
    } finally {
      clearAuthentication();
    }
  }

  if (status === "checking") return <AuthChecking />;
  if (status !== "authenticated") {
    return embedded
      ? (
        <LucimixEntry
          onSignedIn={handleLucimixSignedIn}
          blockedReason={sessionNotKept ? "session_not_kept" : null}
        />
      )
      // 生产只保留 Lucimix 入口，直接访问给引导页；密码登录页仅开发/E2E（vite dev）保留。
      : import.meta.env.DEV
        ? <LoginPage onLogin={handleLogin} />
        : <LucimixOnlyNotice />;
  }
  if (subscriptionBlock) {
    return (
      <SubscriptionGate
        code={subscriptionBlock.code}
        details={subscriptionBlock.details}
        onRecovered={() => {
          setSubscriptionBlock(null);
          setRemountKey((key) => key + 1);
        }}
      />
    );
  }
  return <React.Fragment key={remountKey}>{children({ session, onLogout: handleLogout })}</React.Fragment>;
}


function AuthChecking() {
  return (
    <main className={styles.authPage}>
      <p className={styles.authChecking} role="status">正在确认登录状态…</p>
    </main>
  );
}


function LucimixOnlyNotice() {
  return (
    <main className={styles.authPage} id="main-content">
      <section className={styles.loginCard} aria-labelledby="lucimix-only-title">
        <div className={styles.loginBrand}>
          <span className={styles.loginBrandMark}><ArrowUpRight size={24} aria-hidden="true" /></span>
          <span>Astock Agent</span>
        </div>
        <h1 id="lucimix-only-title">请从 Lucimix 打开</h1>
        <p className={styles.loginLead}>本应用仅通过 Lucimix 访问，请回到 Lucimix 进入 Astock Agent。</p>
      </section>
    </main>
  );
}


function LoginPage({ onLogin }) {
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  async function handleSubmit(event) {
    event.preventDefault();
    if (!username || !password || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await onLogin(username, password);
    } catch (requestError) {
      setError(
        requestError?.payload?.error?.code === "invalid_credentials"
          ? "用户名或密码不正确，请重新输入。"
          : "暂时无法登录，请稍后重试。",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={styles.authPage} id="main-content">
      <section className={styles.loginCard} aria-labelledby="login-title">
        <div className={styles.loginBrand}>
          <span className={styles.loginBrandMark}><ArrowUpRight size={24} aria-hidden="true" /></span>
          <span>Astock Agent</span>
        </div>
        <h1 id="login-title">登录投资市场观察</h1>
        <p className={styles.loginLead}>请使用已授权的账号继续。</p>
        <form className={styles.loginForm} onSubmit={handleSubmit}>
          <label>
            <span>用户名</span>
            <input
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="请输入用户名"
              disabled={submitting}
            />
          </label>
          <label>
            <span>密码</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="请输入密码"
              disabled={submitting}
            />
          </label>
          {error && <p className={styles.loginError} role="alert">{error}</p>}
          <button
            className={styles.loginButton}
            type="submit"
            disabled={!username || !password || submitting}
          >
            {submitting ? "登录中…" : "登录"}
          </button>
        </form>
        <p className={styles.securityNote}>
          <ShieldCheck size={17} aria-hidden="true" />
          仅限授权用户访问
        </p>
      </section>
    </main>
  );
}
